import {
  Flex,
  Icon,
  Select,
  MultiSelect,
  sizes,
  spaces,
  Text,
  type CustomComponents,
} from '@electricjs/arc';
import { useTranslation } from 'react-i18next';

import useDebounce from '@/hooks/useDebounce';
import { type Application } from '@/types/applications';

import ApplicationIcon from '@/components/Applications/ApplicationIcon';
import styled from 'styled-components';

// Type assertion needed because styled-components loses generic type info
const MultiSelectStyled = styled(MultiSelect)`
  .arc-select__control {
    min-height: ${sizes.size_48};
    border-radius: ${sizes.size_8};
    padding: 0 ${spaces.space_xs};
  }
  .arc-select__multi-value {
    height: ${sizes.size_32};
  }
  .arc-select__value-container {
    padding-left: ${spaces.space_xl};
    max-height: 12rem;
    overflow-y: auto;
  }
` as typeof MultiSelect;

type CustomOptionProps = {
  testId: string;
  label: string;
  icon?: string;
  isDisabled: boolean;
  isSelected?: boolean;
};

const CustomOption = ({
  testId,
  label,
  icon,
  isDisabled,
  isSelected,
}: CustomOptionProps) => {
  const textIntent = isDisabled ? 'disabled' : 'primary';

  return (
    <Flex py={spaces.space_xs} alignItems="center">
      <ApplicationIcon name={label} icon={icon} size="24px" />
      <Text
        intent={textIntent}
        variant="label-large"
        ml={spaces.space_xs}
        data-testid={testId}>
        {label}
      </Text>
      {isSelected && (
        <Icon ml="auto" size="small" icon="check" intent="primary" />
      )}
    </Flex>
  );
};

const customComponents: CustomComponents = {
  // eslint-disable-next-line react/prop-types
  Option({ data, label, isSelected }) {
    // eslint-disable-next-line react/prop-types
    const { slug, icon, isDisabled } = data as ApplicationOption;
    return (
      <CustomOption
        testId={slug}
        label={label}
        icon={icon}
        isDisabled={isDisabled}
        isSelected={isSelected}
      />
    );
  },
};

export type ApplicationOption = Application & { isDisabled: boolean };

const customOption = (option: ApplicationOption) => {
  return (
    <CustomOption
      testId={option.slug}
      label={option.name}
      icon={option.icon}
      isDisabled={option.isDisabled}
    />
  );
};

type ApplicationSelectorPropsBase = {
  isFetching: boolean;
  options: ApplicationOption[];
  onFetchData?: (name: string) => void;
};

type SingleSelectProps = ApplicationSelectorPropsBase & {
  isMulti?: false;
  onApplicationSelectedChange: (app: Application | null) => void;
  applicationSelected?: Application | null;
};

type MultiSelectProps = ApplicationSelectorPropsBase & {
  isMulti: true;
  onApplicationSelectedChange: (apps: Application[]) => void;
  applicationSelected?: Application[];
};

type ApplicationSelectorProps = SingleSelectProps | MultiSelectProps;

const ApplicationSelector = (props: ApplicationSelectorProps) => {
  const { t } = useTranslation('applications');
  const {
    onApplicationSelectedChange,
    onFetchData,
    isFetching,
    options,
    applicationSelected,
  } = props;

  const isMulti = props.isMulti ?? false;
  const handleInputChange = useDebounce(onFetchData ?? (() => {}), 500);

  const sharedProps = {
    id: 'add-application-selector',
    isSearchable: true,
    customComponents,
    isLoading: isFetching,
    loadingText: t('selector.loadingApplications'),
    options,
    getOptionLabel: (option: ApplicationOption) => option.name,
    getOptionValue: (option: ApplicationOption) => option.id,
    placeholder: t('selector.searchPlaceholder'),
    isClearable: true,
    onInputChange: handleInputChange,
    formatOptionLabel: customOption,
    showSearchIcon: true,
  };

  if (isMulti) {
    // Build selectedOptions from applicationSelected directly so previously
    // selected chips persist while the user searches for additional apps.
    // The filtered `options` list changes with each keystroke, so looking up
    // selections there would drop chips that no longer appear in results.
    const selectedOptions: ApplicationOption[] =
      (applicationSelected as Application[] | undefined)?.map(app => {
        const found = options.find(opt => opt.id === app.id);
        return found ?? { ...app, isDisabled: false };
      }) ?? [];

    return (
      <MultiSelectStyled<ApplicationOption>
        {...sharedProps}
        hideDropdownIndicator={true}
        onChange={(values: readonly ApplicationOption[]) => {
          // ApplicationOption extends Application, so safe to pass directly
          const apps = [...values];
          (onApplicationSelectedChange as (apps: Application[]) => void)(apps);
        }}
        value={selectedOptions}
      />
    );
  }

  // Convert Application to ApplicationOption by finding match in options
  const selectedOption = (applicationSelected as Application | null | undefined)
    ? (options.find(
        opt => opt.id === (applicationSelected as Application).id
      ) ?? null)
    : null;

  return (
    <Select<ApplicationOption>
      {...sharedProps}
      hideDropdownIndicator={false}
      onChange={(value: ApplicationOption | null) => {
        (onApplicationSelectedChange as (app: Application | null) => void)(
          value
        );
      }}
      value={selectedOption}
    />
  );
};

export { ApplicationSelector };
